import PublicNav from "../../components/PublicNav";
import Footer from "../../components/Footer";
import Seo from "../../components/Seo";

const SECTION_ID = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const SECTIONS = [
  {
    title: "1. Acceptance of these terms",
    body: `Welcome to Mystery Served. These Terms of Service ("Terms") form a legal agreement between you ("you," "your," or "guest") and Mystery Served LLC ("Mystery Served," "we," "us," or "our"). By booking a tour, creating an account, joining a waitlist, redeeming a gift card, or otherwise using the website at https://mysteryserved.com (the "Site") or our tour services (the "Service"), you agree to be bound by these Terms and by our Privacy Policy at https://mysteryserved.com/privacy.

If you do not agree, please do not use the Site or book a tour.`,
  },
  {
    title: "2. Eligibility",
    body: `You must be at least 18 years old to book a tour, provide payment, or accept these Terms on behalf of yourself or your party. By making a booking you represent that:
• You are 18 or older.
• You have the authority to bind every guest in your party to these Terms.
• All information you provide (name, email, phone, billing details, dietary notes) is accurate and current.

Children may attend tours only when accompanied at all times by a booking adult and only when the specific tour explicitly allows minors. Some tours include alcohol or mature themes and are 21+ regardless.`,
  },
  {
    title: "3. Bookings",
    body: `A booking is confirmed only when:
• Payment (or the lead guest's share, for split-pay bookings) has been successfully captured by Stripe, and
• You have received a confirmation email from Mystery Served.

A booking page link (https://mysteryserved.com/booking/<id>) is provided in your confirmation email — this is your source of truth for date, time, headcount, meeting location, and game link.

Capacity is limited. Tours may sell out at any time. We reserve the right to combine, split, or cancel time slots that do not meet a minimum guest count; in that case you will be offered a transfer to another available date or a full refund.

It is your responsibility to:
• Arrive at the meeting location on time. Late arrivals may not be admitted and are not eligible for a refund.
• Provide accurate dietary notes and accessibility needs at booking. We will do our best to accommodate but cannot guarantee changes requested less than 48 hours before the tour.
• Read tour-day instructions and game-link emails before arriving.`,
  },
  {
    title: "4. Payments",
    body: `All prices are listed in U.S. Dollars (USD) and are charged at the time of booking via Stripe.

Pricing on a tour page includes the experience itself and any items explicitly listed ("includes" line on the tour page). Alcoholic beverages, gratuities, parking, and items purchased at clue-stop venues are NOT included unless explicitly stated and are payable directly to the venue.

Sales tax, payment processing fees, and any applicable local fees are calculated and displayed at checkout.

Split-pay bookings: For tours that enable split-pay, the lead guest pays their share at booking and each co-guest receives a private payment link via email and/or SMS. The booking is held against capacity but a co-guest's seat is not finalized until their share is paid. We reserve the right to release unpaid co-guest seats up to 2 hours before the tour begins.

Gift cards: Gift cards are sold in fixed denominations, are non-refundable once issued, and expire per the date printed on the card (or 1 year from purchase if not stated). Gift cards may be applied to any public tour at checkout and may not be redeemed for cash.

Promotional codes: Promo codes are limited to the terms displayed at the point of redemption and may not be combined with other offers unless explicitly allowed.

Chargebacks: Initiating a chargeback without first contacting Mystery Served at info@mysteryserved.com to attempt resolution is a violation of these Terms and may result in legal cost recovery and a permanent ban from booking with us.`,
  },
  {
    title: "5. Refunds, cancellations & rescheduling",
    body: `Guest-initiated cancellation:
• More than 7 days before the tour start time: full refund, less a 5% payment-processor fee.
• 7 days to 48 hours before the tour start time: 50% refund OR full credit toward a future tour booked within 12 months, at your choice.
• Less than 48 hours before the tour start time: no refund. We may, at our sole discretion, offer credit toward a future tour for documented emergencies (illness, bereavement, etc.).
• No-shows are not refundable.

Guest-initiated rescheduling:
• More than 48 hours before the tour: free reschedule subject to availability.
• Less than 48 hours: treated as a cancellation under the rules above.

Mystery Served-initiated cancellation (weather, low headcount, operational issues, force majeure):
• You will be offered a transfer to a comparable future tour OR a full refund (including processor fees), at your choice.
• We are not responsible for non-refundable travel, lodging, or other costs you may have incurred.

To request a cancellation or reschedule, email info@mysteryserved.com from the email address used to book, or use the link in your confirmation email. Refunds are issued to the original payment method and typically settle within 5–10 business days.`,
  },
  {
    title: "6. Guest conduct",
    body: `Mystery Served tours take place on city streets and in third-party venues (restaurants, shops, galleries, bars). By participating you agree to:
• Treat guides, staff, fellow guests, and venue partners with respect.
• Follow all reasonable instructions from your guide, including instructions related to safety, pacing, and venue rules.
• Obey all applicable laws, including traffic, pedestrian, alcohol, and public-conduct laws.
• Refrain from disruptive, threatening, harassing, intoxicated, or otherwise disorderly behavior.
• Refrain from discriminatory speech or conduct toward any guest, guide, or venue staff.
• Pay any tabs, gratuities, or damages directly to the venues for items not included in the ticket price.

We reserve the right to remove any guest from a tour without refund for behavior that, in our sole judgment, violates these standards or endangers the safety or experience of others. Permanent bans from future bookings may be issued in egregious cases.`,
  },
  {
    title: "7. Liability waiver & assumption of risk",
    body: `IMPORTANT — READ CAREFULLY.

You understand and acknowledge that Mystery Served tours involve walking on uneven sidewalks, crossing streets, entering and exiting third-party venues, and participating in a live, interactive experience that may include puzzles, role-play, mild physical activity, and the consumption of food and beverages. There are inherent risks, including but not limited to: slips, trips, falls, traffic, weather conditions, food allergies, and the conduct of other patrons or third parties.

By booking and attending a tour you agree, on behalf of yourself and every member of your party, that:
• You voluntarily assume all risks associated with participation.
• You are physically and mentally able to participate, or you have disclosed any limitations to us at booking so we may offer reasonable accommodations.
• You are responsible for disclosing food allergies and dietary restrictions. While we will share your notes with venues, we cannot guarantee a venue's preparation environment is free of any allergen.
• To the fullest extent permitted by law, you release, waive, and discharge Mystery Served LLC, its owners, employees, contractors, guides, and affiliated venues from any and all claims, demands, damages, injuries, or losses arising out of or related to your participation, except to the extent caused by Mystery Served's gross negligence or willful misconduct.
• If you bring a minor or another adult into your party, you accept this waiver on their behalf and represent you have authority to do so.

This waiver does not limit any rights that cannot be waived under applicable law.`,
  },
  {
    title: "8. Photo, video & content release",
    body: `Tours are interactive group experiences and may be photographed or filmed by Mystery Served for promotional, social-media, and operational purposes. By attending you grant Mystery Served a non-exclusive, royalty-free, worldwide license to use your likeness, voice, and any images or video captured during the tour for marketing and editorial purposes, without further notice or compensation.

If you do not wish to be photographed or filmed, please tell your guide at the start of the tour and we will do our best to accommodate.

Any photos, videos, or written content you post publicly that depict Mystery Served, our guides, our venues, or other guests should be respectful and accurate. We may ask you to remove content that violates these Terms or that exposes us, our venues, or other guests to harm.`,
  },
  {
    title: "9. Intellectual property",
    body: `All content on the Site — including the Mystery Served name and logo, tour names, story content, puzzle and clue materials, photographs, videos, copy, page designs, and the game-link experience — is the property of Mystery Served LLC or its licensors and is protected by U.S. and international copyright, trademark, and other intellectual-property laws.

You may not copy, reproduce, distribute, modify, create derivative works from, publicly display, or commercially exploit any of this content without our prior written permission. You may share publicly visible tour pages and your own booking link for personal, non-commercial purposes (for example, telling friends about a tour).`,
  },
  {
    title: "10. Third-party venues, links & services",
    body: `Mystery Served partners with independent restaurants, shops, and venues ("clue stops"). These venues are independent businesses; their food, drinks, service, and conduct are their own responsibility. Issues with food quality, service, or charges incurred at a venue should be raised with that venue directly. We will gladly assist in escalating where appropriate.

The Site may contain links to third-party websites (including Stripe, Twilio, Resend, Google Maps, and our venue partners). We do not control and are not responsible for the content, privacy practices, or terms of any third-party site or service.`,
  },
  {
    title: "11. Disclaimers",
    body: `THE SITE AND THE SERVICE ARE PROVIDED "AS IS" AND "AS AVAILABLE." TO THE FULLEST EXTENT PERMITTED BY LAW, MYSTERY SERVED DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND COURSE OF PERFORMANCE.

WE DO NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE; THAT DEFECTS WILL BE CORRECTED; OR THAT THE SITE OR ANY SERVERS HOSTING IT ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.

Some jurisdictions do not allow disclaimers of implied warranties, so some of the above may not apply to you.`,
  },
  {
    title: "12. Limitation of liability",
    body: `TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL MYSTERY SERVED, ITS OWNERS, EMPLOYEES, CONTRACTORS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, LOST DATA, LOSS OF GOODWILL, OR ANY OTHER INTANGIBLE LOSSES ARISING OUT OF OR RELATED TO YOUR USE OF THE SITE OR PARTICIPATION IN A TOUR, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL THEORY, EVEN IF MYSTERY SERVED HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.

MYSTERY SERVED'S TOTAL CUMULATIVE LIABILITY ARISING OUT OF OR RELATED TO THESE TERMS, THE SITE, OR THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE TOTAL AMOUNT YOU PAID TO MYSTERY SERVED IN THE 12 MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS (US$100).

Some jurisdictions do not allow these limitations, so some may not apply to you. In those jurisdictions, our liability is limited to the maximum extent permitted by law.`,
  },
  {
    title: "13. Indemnification",
    body: `You agree to defend, indemnify, and hold harmless Mystery Served LLC, its owners, employees, contractors, guides, and affiliates from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to:
• Your violation of these Terms or our Privacy Policy.
• Your violation of any law or the rights of any third party.
• Your participation in a tour, including any conduct that injures another guest, staff member, or venue.
• Any content you submit, post, or transmit through the Site.`,
  },
  {
    title: "14. Governing law & dispute resolution",
    body: `These Terms, the Site, and the Service are governed by and construed in accordance with the laws of the State of Florida, U.S.A., without regard to its conflict-of-law principles.

Any dispute, claim, or controversy arising out of or relating to these Terms, the Site, or the Service shall be resolved exclusively in the state or federal courts located in Pinellas County, Florida, and you consent to the personal jurisdiction and venue of those courts.

Before filing any formal claim, you agree to first attempt in good faith to resolve the dispute by contacting Mystery Served at info@mysteryserved.com and giving us 30 days to respond.

Class-action waiver: You and Mystery Served agree that any dispute will be resolved on an individual basis only and not as a plaintiff or class member in any purported class, consolidated, or representative action.`,
  },
  {
    title: "15. Termination",
    body: `We may suspend, restrict, or terminate your access to the Site, your bookings, or the Service at any time, with or without notice, for any conduct that we, in our sole judgment, believe violates these Terms, harms other guests or our venues, or otherwise is not in the best interest of Mystery Served.

Sections that by their nature should survive termination (including Liability Waiver, Intellectual Property, Disclaimers, Limitation of Liability, Indemnification, and Governing Law) will survive.`,
  },
  {
    title: "16. Changes to these terms",
    body: `We may update these Terms from time to time. The "Last updated" date at the top reflects the most recent revision. Material changes will be announced by email (if you have provided one) and by prominent notice on the Site before they take effect. Your continued use of the Site or booking of tours after the effective date constitutes acceptance of the updated Terms.`,
  },
  {
    title: "17. Contact",
    body: `Questions about these Terms? Need to cancel, reschedule, or report an issue? We're here.

Mystery Served LLC
Email: info@mysteryserved.com
Web: https://mysteryserved.com
Mail: see our site footer for our current mailing address.`,
  },
];

export default function Terms() {
  const lastUpdated = "February 22, 2026";

  return (
    <div className="public-shell">
      <Seo
        title="Terms of Service"
        description="Mystery Served LLC Terms of Service: bookings, payments, refunds, cancellations, guest conduct, liability waiver, and Florida governing law."
        url="https://mysteryserved.com/terms"
      />
      <PublicNav />

      {/* HERO */}
      <section className="bg-ms-cream pt-40 md:pt-48 pb-12" data-testid="terms-hero">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center animate-fade-up">
          <p className="label-eyebrow text-ms-brass" data-testid="terms-eyebrow">Legal</p>
          <h1 className="serif text-ms-ink text-5xl md:text-6xl mt-5 leading-[0.95]" data-testid="terms-headline">
            Terms of <span className="italic text-ms-brass">Service</span>
          </h1>
          <p className="mt-6 text-ms-stone text-sm uppercase tracking-[0.28em]" data-testid="terms-last-updated">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-ms-cream pb-24" data-testid="terms-body">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* TOC */}
          <nav className="bg-white border border-ms-edge rounded-2xl p-6 md:p-8 mb-12" data-testid="terms-toc">
            <p className="label-eyebrow text-ms-brass">Contents</p>
            <ul className="mt-4 space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.title}>
                  <a
                    href={`#${SECTION_ID(s.title)}`}
                    className="text-ms-ink/80 hover:text-ms-brass underline-offset-4 hover:underline"
                    data-testid={`terms-toc-${SECTION_ID(s.title)}`}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {SECTIONS.map((s) => (
            <article key={s.title} id={SECTION_ID(s.title)} className="mb-12 scroll-mt-32" data-testid={`terms-section-${SECTION_ID(s.title)}`}>
              <h2 className="serif text-ms-ink text-2xl md:text-3xl leading-[1.15]">
                {s.title}
              </h2>
              <div className="mt-5 text-ms-stone text-base leading-[1.8] whitespace-pre-line">
                {s.body}
              </div>
            </article>
          ))}

          <div className="hairline my-12" />
          <p className="text-ms-stone text-sm leading-[1.7]">
            For questions about these Terms, contact us at{" "}
            <a href="mailto:info@mysteryserved.com" className="text-ms-brass hover:underline">
              info@mysteryserved.com
            </a>
            . See also our{" "}
            <a href="/privacy" className="text-ms-brass hover:underline">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
